// src/components/property/video-settings/MyTemplatesTab.tsx
/**
 * MyTemplatesTab Component
 * Tab for managing user-created custom templates
 *
 * Feature: Video Settings
 */

import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { UserTemplateForm } from './UserTemplateForm';
import type {
  IMyTemplatesTabProps,
  IUserPromptTemplate,
  IUserTemplateFormData,
} from '@/@types/videoSettings';
import type { Id } from '../../../../convex/_generated/dataModel';

const CATEGORY_LABELS: Record<string, string> = {
  building: 'Building',
  outdoor: 'Outdoor',
  interior: 'Interior',
  other: 'Other',
};

function MyTemplatesTab({
  templates,
  onCreate,
  onUpdate,
  onDelete,
  isLoading,
}: IMyTemplatesTabProps) {
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<IUserPromptTemplate | null>(null);
  const [deletingId, setDeletingId] = useState<Id<'userPromptTemplates'> | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCreate = useCallback(
    async (data: IUserTemplateFormData) => {
      setIsSubmitting(true);
      try {
        await onCreate(data);
        setShowForm(false);
      } finally {
        setIsSubmitting(false);
      }
    },
    [onCreate]
  );

  const handleUpdate = useCallback(
    async (data: IUserTemplateFormData) => {
      if (!editingTemplate) return;
      setIsSubmitting(true);
      try {
        await onUpdate(editingTemplate._id, data);
        setEditingTemplate(null);
      } finally {
        setIsSubmitting(false);
      }
    },
    [editingTemplate, onUpdate]
  );

  const handleDelete = useCallback(async () => {
    if (!deletingId) return;
    await onDelete(deletingId);
    setDeletingId(null);
  }, [deletingId, onDelete]);

  const handleCancel = useCallback(() => {
    setShowForm(false);
    setEditingTemplate(null);
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-40" />
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      </div>
    );
  }

  // Show form for creating or editing
  if (showForm || editingTemplate) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">
          {editingTemplate ? 'Edit Template' : 'Create Template'}
        </h3>
        <UserTemplateForm
          initialData={
            editingTemplate
              ? {
                  name: editingTemplate.name,
                  positivePrompt: editingTemplate.positivePrompt,
                  negativePrompt: editingTemplate.negativePrompt,
                  categories: editingTemplate.categories,
                }
              : undefined
          }
          onSubmit={editingTemplate ? handleUpdate : handleCreate}
          onCancel={handleCancel}
          isSubmitting={isSubmitting}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Create your own video templates with custom prompts. Your templates are private and only
          visible to you.
        </div>
        <Button onClick={() => setShowForm(true)} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Create Template
        </Button>
      </div>

      {templates.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
          <p>No custom templates yet.</p>
          <p className="text-sm mt-1">Create your first template to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map(template => (
            <div
              key={template._id}
              className="p-4 bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-lg"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">{template.name}</h4>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {template.categories.map(cat => (
                      <Badge key={cat} variant="secondary" className="text-xs">
                        {CATEGORY_LABELS[cat]}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 line-clamp-2">
                    {template.positivePrompt}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => setEditingTemplate(template)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setDeletingId(template._id)}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deletingId} onOpenChange={() => setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this template? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export { MyTemplatesTab };
